import { useState } from 'react'
import {
  Grid,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
} from '@mui/material'
import AircraftSelector from './AircraftSelector'
import { useQuery } from '@apollo/client'
import { GET_LINKED_TASKS } from './queries'

interface LinkedTask {
  id: string
  title: string
  ataCode: string
  manufactureCode: string
  description: string
}

interface TaskRelationship {
  id: string
  task1: LinkedTask
  task2: LinkedTask
  relationship: string
  isDirectional: boolean
  createdAt: string
}

const ViewLinkedTasksTab = () => {
  const [selectedAircraftId, setSelectedAircraftId] = useState<string | null>(
    null
  )

  const { data, loading, error } = useQuery(GET_LINKED_TASKS, {
    variables: { aircraftId: selectedAircraftId },
    skip: !selectedAircraftId,
  })

  const relationships: TaskRelationship[] = data?.linkedTasks || []

  return (
    <Grid container spacing={3}>
      {/* Aircraft Selection */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Select Aircraft
            </Typography>
            <AircraftSelector
              selectedAircraftId={selectedAircraftId}
              onAircraftSelect={setSelectedAircraftId}
            />
          </CardContent>
        </Card>
      </Grid>

      {/* Error Message */}
      {error && (
        <Grid item xs={12}>
          <Alert severity="error">{error.message}</Alert>
        </Grid>
      )}

      {/* Linked Tasks Display */}
      {selectedAircraftId && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Linked Tasks
              </Typography>

              {loading ? (
                <Typography>Loading linked tasks...</Typography>
              ) : relationships.length === 0 ? (
                <Alert severity="info">
                  No linked tasks found for this aircraft.
                </Alert>
              ) : (
                <List>
                  {relationships.map((relationship, index) => (
                    <div key={relationship.id}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle1">
                              {relationship.task1.title}{' '}
                              {relationship.isDirectional ? '→' : '↔'}{' '}
                              {relationship.task2.title}
                            </Typography>
                          }
                          secondary={
                            <div>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Task 1: {relationship.task1.ataCode} -{' '}
                                {relationship.task1.manufactureCode}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Task 2: {relationship.task2.ataCode} -{' '}
                                {relationship.task2.manufactureCode}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Relationship: {relationship.relationship}
                                {relationship.isDirectional && ' (Directional)'}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Linked:{' '}
                                {new Date(
                                  relationship.createdAt
                                ).toLocaleDateString()}
                              </Typography>
                            </div>
                          }
                        />
                      </ListItem>
                      {index < relationships.length - 1 && <Divider />}
                    </div>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  )
}

export default ViewLinkedTasksTab
